import * as utils from '../utils';
import * as perf from '../commons/performance-logger';
import { BaseWorker } from './BaseWorker';
import { getLogger } from '../commons/logger';
import { reporter } from '../reports/reporter';
import { preloadTests } from '../commons/preloadTests';
import { CLI_MODE, timeoutMessages } from '../commons/constants';
import { WindowUtils } from '../player/utils/windowUtils';
import { PageNotAvailableError, TimeoutError } from '../errors';
import { SeleniumTestPlayer } from '../player/seleniumTestPlayer';
import { getSessionPlayer } from '../commons/getSessionPlayerRequire';
import { gridAccessWhitelistedIp, wasInitializedFromCache } from '../commons/testimCustomToken';
import type { FailedSessionPlayerTestResult } from 'clickim/src/background/session/sessionPlayer';
import type { PlaybackEvent } from 'clickim/src/background/playbackManagerEventsMapping';
import type { TestResult } from 'clickim/src/common/models/TestResult';
import type { TestResultWithStats } from './BaseWorker';
import type { TestRunHandler } from '../testRunHandler';

const logger = getLogger('worker-selenium');

// this navigation timeout is handled from outside the worker, so don't pass a small timeout to navigate
const NO_NAVIGATION_TIME_LIMIT = 1e9;

export class WorkerSelenium extends BaseWorker<SeleniumTestPlayer> {
    private windowUtils?: WindowUtils;

    override initPlayer(testRunHandler: TestRunHandler) {
        return new SeleniumTestPlayer(
            this.id,
            testRunHandler.runParams,
            this.options.shouldMonitorPerformance,
            undefined,
            testRunHandler.retryCount,
            testRunHandler.previousTestResultId,
        );
    }

    override async getBrowserOnce(testRunHandler: TestRunHandler, customExtensionLocalLocation: string | undefined, seleniumTestPlayer: SeleniumTestPlayer, gridInfo: any) {
        perf.log('in WorkerSelenium getBrowserOnce');
        reporter.onGetSession(this.id, this.testName, testRunHandler.runMode);
        const { driver } = seleniumTestPlayer;

        this.windowUtils = new WindowUtils(this.id, driver);
        seleniumTestPlayer.clearSessionTabs();

        const { browserValue } = this.testRunConfig as { browserValue: string };
        const baseUrl = testRunHandler.baseUrl;

        try {
            const fastInit = this.options.useLocalChromeDriver;
            await driver.init({
                projectData: this.options.projectData,
                browserOptions: this.options,
                testName: this.testName!,
                testRunConfig: this.testRunConfig!,
                gridInfo,
                customExtensionLocalLocation,
                executionId: this.executionId,
                testResultId: this.testResultId!,
                seleniumPerfStats: testRunHandler.seleniumPerfStats,
                fastInit,
                lambdatestService: this.lambdatestService,
                printFinalCaps: this.options.printFinalCaps,
                mode: CLI_MODE.SELENIUM,
                customCapabilities: testRunHandler.customCapabilities,
            });

            perf.log('in WorkerSelenium after driver.init');
            await seleniumTestPlayer.addTab(undefined, { skipLoadInfo: fastInit });
            perf.log('in WorkerSelenium after addTab');
            if (!fastInit) {
                await this.windowUtils.navigate(baseUrl!, NO_NAVIGATION_TIME_LIMIT);
            }
            await this.windowUtils.validatePageIsAvailable();
            perf.log('in WorkerSelenium after navigate');
        } catch (err) {
            const firefoxPageNotAvailable = err.message && (err.message.startsWith('Malformed URL') || err.message.includes('Reached error page: about:neterror')) &&
                browserValue === 'firefox';

            const invalidURL = (err.message && err.message === 'invalid argument');

            if (err instanceof PageNotAvailableError || firefoxPageNotAvailable || invalidURL) {
                throw new PageNotAvailableError(`Page '${baseUrl}' is not available`);
            }

            logger.error('failed to navigate to page', {
                baseUrl,
                err,
                whitelistedPublicIp: gridAccessWhitelistedIp(),
                initializedFromCache: wasInitializedFromCache(),
            });
            throw err;
        }
    }

    override async runTestOnce(testRunHandler: TestRunHandler, seleniumTestPlayer: SeleniumTestPlayer) {
        const { manifestVersion, localAssetService } = getSessionPlayer();
        const { driver, sessionPlayer } = seleniumTestPlayer;

        const version = manifestVersion || 'runner';

        reporter.onWaitToTestComplete(this.id);

        setupCliPerformanceMonitoring(sessionPlayer);

        sessionPlayer.playbackManager.executionId = testRunHandler.executionId;
        sessionPlayer.playbackManager.executionName = testRunHandler.executionName;
        // Mode can be undefined, we don't want to pass turbo mode options to Selenium player.
        if (this.options.mode === CLI_MODE.SELENIUM) {
            sessionPlayer.setLightweightMode(this.options.lightweightMode);
        }
        if (testRunHandler.sfdcCredential) {
            sessionPlayer.setSfdcCredential(testRunHandler.sfdcCredential);
        }
        localAssetService.initialize({ serverUrl: this.options.localRCASaver });

        let preloadedTest: any = null;
        if (this.options.lightweightMode?.preloadTests) {
            const preloadedTests = await preloadTests(this.options);
            preloadedTest = preloadedTests[this.testId!];
        }

        const runSeleniumTest = async () => utils.promiseTimeout(
            // TODO: Fix promise constructor anti-pattern
            new Promise<TestResult | FailedSessionPlayerTestResult>((resolve, reject) => {
                sessionPlayer.playByTestId({
                    testId: this.testId!,
                    executionId: this.executionId,
                    resultId: this.testResultId!,
                    baseUrl: this.baseUrl,
                    userData: this.userData!,
                    version,
                    resolve,
                    isLocalRun: false,
                    overrideTestConfigId: this.overrideTestConfigId,
                    branch: this.branch!,
                    incognito: false,
                    remoteRunId: testRunHandler.remoteRunId,
                    preloadedTest,
                    smartUIIntegrationData: testRunHandler.smartUIIntegrationData,
                }).catch(reject);
            }),
            this.testRunTimeout,
            timeoutMessages.TEST_COMPLETE_TIMEOUT_MSG,
        )
            .catch((err) => {
                if (err instanceof TimeoutError) {
                    sessionPlayer.stopPlayingOnTestTimeout?.();
                }
                throw err;
            })
            .then(async (testResult) => {
                await localAssetService.drain();
                testResult.stepsResults = null;
                testResult.resultId = this.testResultId!;
                if (!driver.isAlive()) {
                    logger.warn(`possible grid unresponsive for test ${this.testId}, result ${this.testResultId} (execution: ${this.executionId})`);
                    testResult.gridIssues = 'could not validate grid is alive';
                }
                const maxKeepAliveGap = driver.maxKeepAliveGap();
                const MAX_KEEP_ALIVE_GAP = 30000;
                if (maxKeepAliveGap >= MAX_KEEP_ALIVE_GAP) {
                    logger.warn(`possible browser keep alive issue ${this.testId}, result ${this.testResultId} (execution: ${this.executionId})`);
                    testResult.keepAliveIssue = maxKeepAliveGap;
                }
                const resultWithStats: TestResultWithStats = { ...testResult, ...testRunHandler.seleniumPerfStats.getStats() } as TestResultWithStats;
                if (this.lambdatestService.isLambdatestRun()) {
                    await driver.executeJS(`lambda-status=${!resultWithStats.success ? 'failed' : 'passed'}`).catch(() => { /* ignore */ });
                }
                return resultWithStats;
            });

        driver.start();

        perf.log('right before super.runTestOnce in workerSelenium');
        try {
            await super.runTestOnce(testRunHandler, seleniumTestPlayer);
            perf.log('right after super.runTestOnce in workerSelenium');
            const testResult = await runSeleniumTest();
            perf.log('right after runSeleniumTest');
            return testResult;
        } catch (err) {
            logger.error('failed to run test once', { err });
            throw err;
        }
    }
}

function setupCliPerformanceMonitoring(sessionPlayer: SeleniumTestPlayer['sessionPlayer']) {
    const { playback } = getSessionPlayer().commonConstants;
    for (const event of Object.values(playback)) {
        sessionPlayer.playbackManager.on(event as PlaybackEvent, () => perf.log(`Got event ${event}`));
    }
}
